import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { LanguageType } from '@/i18n'
import { AdditionalGuestInput, NationalityType } from '@/models/self-checkin/accommodation-info'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { SelfCheckInState } from '@/models/self-checkin'
import { v4 as uuidv4 } from 'uuid'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { SelfCheckinContentCard } from '@/components/molecules/self-checkin-content-card'
import { AdditionalGuestForm } from '@/components/organisms/self-checkin/additional-guest-form'

export const AdditionalGuestInfo: React.FC<{}> = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [isFormValidRequiredFields, setIsFormValidRequiredFields] = useState(true)
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const additionalGuestInfoInitialData: AdditionalGuestInput = {
    _type: 'additionalGuest',
    nationality: lang === LanguageType.ja ? NationalityType.Jp : NationalityType.Njp,
    firstName: '',
    lastName: '',
    paxTotal: state.selectReservation?.paxTotal || 1,
    accompany: [],
    hasContactForAllGuest: !state.setting.accommodation.requiredAccompanyNameInput,
  }
  const [additionalGuestInfo, setAdditionalGuestInfo] = useState<AdditionalGuestInput>(additionalGuestInfoInitialData)

  const listValidateProps = useRef(['firstName', 'lastName'])

  const isDisabled = (): boolean => {
    const isValid = checkValidateFormRequired()
    return !isValid
  }

  const checkValidateFormRequired = () => {
    for (const property in additionalGuestInfo) {
      if (listValidateProps.current.includes(property)) {
        if (
          !additionalGuestInfo[property] ||
          (typeof additionalGuestInfo[property] === 'string' && !additionalGuestInfo[property].trim())
        ) {
          return false
        }
      }
    }
    const validAccompany = additionalGuestInfo.accompany?.filter(v => !v.lastName.trim() || !v.firstName.trim()) ?? []
    if (!additionalGuestInfo.hasContactForAllGuest && validAccompany.length > 0) {
      return false
    }

    return true
  }

  const checkOnlyAnphabet = value => {
    return /^[a-zA-Z\s]*$/.test(value)
  }

  const onChangeAdditionalGuestInfo = (name, value) => {
    if (listValidateProps.current.includes(name)) {
      if (typeof value === 'string' && !value.trim()) {
        setIsFormValidRequiredFields(false)
      }
    }
    setAdditionalGuestInfo(accomInfo => ({ ...accomInfo, [name]: value }))
  }

  const submit = async () => {
    setIsFirstNameValid(true)
    setIsLastNameValid(true)
    const isValid = checkValidateFormRequired()
    setIsFormValidRequiredFields(isValid)
    if (isValid) {
      const checkLastName = !(additionalGuestInfo.nationality === NationalityType.Jp && lang === 'ja')
        ? checkOnlyAnphabet(additionalGuestInfo.lastName)
        : true
      const checkFirstName = !(additionalGuestInfo.nationality === NationalityType.Jp && lang === 'ja')
        ? checkOnlyAnphabet(additionalGuestInfo.firstName)
        : true

      setIsFirstNameValid(checkFirstName)
      setIsLastNameValid(checkLastName)

      const isValidForm = checkFirstName && checkLastName
      if (!isValidForm) {
        // set the name of the error message
        let scrollName = ''
        switch (true) {
          case !checkLastName:
            scrollName = 'lastName'
            break
          case !checkFirstName:
            scrollName = 'firstName'
            break
          default:
            // scroll to top
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
            break
        }
        if (scrollName) {
          // get element by name
          const scrollErrorName = document.getElementsByName(scrollName)
          // Scroll to target element
          scrollErrorName[0]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
        return
      }
      saveState({ ...state, accommodationInfo: additionalGuestInfo })

      history.push({ pathname: '/self-checkin/checkin-customize' })
    }
  }

  const getReservationDetail = useCallback((selectReservation: Required<SelfCheckInState>['selectReservation']) => {
    const paxTotal = selectReservation.paxTotal || 1
    setAdditionalGuestInfo({
      ...additionalGuestInfo,
      ...{
        accompany: Array.from(new Array(paxTotal - 1)).map(() => {
          return { id: uuidv4(), lastName: '', firstName: '', name: '' }
        }),
      },
    })
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }
    setIsInitializeComponent(true)
    if (state.accommodationInfo) {
      setAdditionalGuestInfo(state.accommodationInfo as AdditionalGuestInput)
    } else if (state.selectReservation) {
      getReservationDetail(state.selectReservation)
    }

    if (state.setting.accommodation.requiredAccompanyNameInput) {
      listValidateProps.current.push('accompany')
    }
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Enter check-in information') }}
      footer={{ right: { isDisabled: isDisabled(), label: t('Next'), onClick: submit } }}
    >
      <SelfCheckinContentCard>
        <AdditionalGuestForm
          additionalGuestInfo={additionalGuestInfo}
          onChange={onChangeAdditionalGuestInfo}
          requiredAccompanyNameInput={state.setting.accommodation.requiredAccompanyNameInput}
          isFormValidRequiredFields={isFormValidRequiredFields}
          isFirstNameValid={isFirstNameValid}
          isLastNameValid={isLastNameValid}
        />
      </SelfCheckinContentCard>
    </SelfCheckinLayout>
  )
}
